import {
  newLinearElement,
  newMindmapNodeElement,
} from "../../excalidraw/element/newElement";
import { newElementWith } from "../../excalidraw/element/mutateElement";
import type {
  ExcalidrawElement,
  ExcalidrawMindmapNodeElement,
} from "../../excalidraw/element/types";
import { INTEGRATION_STYLE } from "../../excalidraw/constants";
import { getSizeFromPoints } from "../../excalidraw/points";

import type { Grouping } from "./mindmapDbTree";
import type { MindmapNodeProvenance, MindmapTree } from "./mindmapTree";
import type { MindmapLayout, MindmapLayoutNode } from "./mindmapLayout";
import { assignBranchColors, MINDMAP_NEUTRAL_COLOR } from "./mindmapColors";
import { mindmapConnectorGeometry } from "./mindmapConnectorGeometry";
import { withMindmapBindings } from "./mindmapBinding";

/**
 * Element emitter for the auto-mindmap.
 *
 * Turns a laid-out tree into real Excalidraw elements. In V2 every map node —
 * block AND page — becomes a `mindmapNode` pill card (DOM-rendered by
 * RenderMindmapNode): page pills carry `pageId` so the renderer draws the Notion
 * glyph and opens the side peek on body click; the expand chip is a separate
 * click target. Each edge becomes a curved arrow whose geometry is bounded by
 * mindmapConnectorGeometry. Every element carries
 *   customData.mindmap = { mapId, blockId, role, parentBlockId?, sourcePageId? }
 * where `role` is "node" for a mapped block and "connector" for an arrow (the
 * arrow's `blockId` is the child it points to). `parentBlockId` lets a collapse
 * reconstruct a node's subtree; `sourcePageId` is the page whose recordMap the
 * block lives in, so expansion and refresh re-walk the right page.
 * This identity is what makes a later refresh diff the map against the canvas.
 */

/** Whether a map was generated from a Notion page or a database. */
export type MindmapSource = "page" | "database";

/** Payload written to `customData.mindmap` on every generated element. */
export interface MindmapCustomData {
  mapId: string;
  blockId: string;
  role: "node" | "connector";
  /**
   * The Notion id this map was generated from (the root node's blockId): a page
   * id for a page map, a database id for a database map. Carried on every
   * element so a refresh triggered from any selected element can recover what to
   * refetch — durable through save/reload alongside the rest of `customData`.
   */
  rootPageId: string;
  /**
   * Where the map came from, so refresh routes to the right fetch + tree builder
   * (page recordMap vs database query). Absent on legacy page maps generated
   * before database maps existed — refresh treats a missing source as "page".
   */
  source?: MindmapSource;
  /**
   * For a grouped database map, the select/status property the rows were grouped
   * by, so a refresh regroups consistently. Absent for page or flat maps.
   */
  groupingProperty?: string;
  /**
   * Map ROOT node only: the large-flat-database nudge has already been shown
   * for this map, so it never fires twice. See `maybeNudgeLargeFlatDatabase`.
   */
  largeFlatNudged?: boolean;
  /**
   * Map ROOT node only: the select/status properties this database could be
   * grouped by, so the Group by popover opens from the scene instead of querying
   * Notion on every open (the owner's "why does it load every time"). Names and
   * types only — the values live in the rows, not here. Refreshed by every
   * refresh, so a property added in Notion shows up after one.
   */
  groupingCandidates?: Grouping[];
  /** True on a "+N more" truncation stub — no real block to diff on refresh. */
  stub?: boolean;
  /**
   * True on the skeleton pill that holds the map's spot while generation runs.
   * Not a real map node: the renderer pulses it and every diff/expand path
   * ignores it. Declared here because it used to be written through an untyped
   * `Record<string, any>` and nothing checked the spelling.
   */
  placeholder?: boolean;
  /**
   * Why a tombstoned (`isDeleted`) element was removed. Refresh's resurrection
   * exclusion reads it: a node IT removed (source "refresh") may come back when
   * the block reappears in Notion, while one the user deleted stays dead. A
   * tombstone with no provenance is treated as a user deletion — the safe
   * default, since that is what every pre-V3 tombstone was.
   *
   * "history" is an undo that took an element back out of existence (the
   * children of an expand, say). It is not a deletion of anything: without the
   * stamp, undoing an expand looked exactly like the user deleting every child,
   * and the recorder wrote them all into the ledger — suppressing those blocks
   * from every later expand and refresh, permanently.
   *
   * "agent" is a deletion made through the MCP write tools: it stays
   * dead on refresh exactly like a user deletion, and its ledger entry says so.
   */
  removedBy?: "refresh" | "user" | "collapse" | "history" | "agent";
  /**
   * Map ROOT node only: one entry per user deletion, remembered so a refresh
   * does not resurrect them. See the deletion-ledger section below for why the
   * memory cannot live on the tombstones themselves.
   */
  deletions?: MindmapDeletionEntry[];
  /**
   * The pre-entry ledger: a flat list of deleted blockIds. Read-only — scenes
   * saved with it still suppress correctly, and the next write migrates them
   * into `deletions` and drops this field.
   */
  deletedBlockIds?: string[];
  /**
   * Native (`nodeKind: "text"`) nodes only: the link the node stands for (the
   * agent tools call it `source`; `source` above is the map's origin).
   * Validated by `restore`.
   */
  provenance?: MindmapNodeProvenance;
  /** Explicit agent palette name for this node; absent on inherited colours. */
  colorName?: string;
  /**
   * The parent node's blockId. Present on every non-root node, so a collapse
   * can reconstruct a node's on-canvas subtree from the scene at any time
   * (after edits, save/reload) by walking these links. Absent on the root and
   * on connectors.
   */
  parentBlockId?: string;
  /**
   * The Notion page whose recordMap this node's block lives in. Block nodes
   * expand by re-fetching this page; refresh re-walks each expanded source.
   * A page node's OWN children come from `pageId`, not `sourcePageId`
   * (which is the page the page-node itself sits in). Defaults to the map root.
   */
  sourcePageId?: string;
  /**
   * A collapsed node's remembered subtree: the generated node/connector elements
   * that were removed from the scene, serialized verbatim (positions + user
   * edits) so re-expand restores them without a fetch. Only set on a node whose
   * `expanded` is false after a collapse. Bounded — skeleton subtrees are small.
   */
  remembered?: RememberedSubtree;
  /**
   * Set by a refresh when the collapsed node's source was re-walked: the
   * remembered subtree is NOT auto-updated (refresh stays cheap), but is now
   * potentially stale, so the next expand re-fetches instead of restoring it
   * verbatim — restoring stale content would violate "refresh = fresh".
   */
  rememberedStale?: boolean;
  /**
   * Connectors only: where along the parent edge this connector departs, as a
   * fraction in [0,1]. Persisted so a reflow that re-routes the
   * connector after moving its endpoints keeps the same fanned-out departure
   * instead of collapsing back to the piled-up center.
   */
  departRatio?: number;
}

/**
 * A collapsed node's remembered subtree. `elements` are the exact
 * generated node + connector elements removed on collapse, kept so a re-expand
 * re-inserts them with every user edit intact and no re-fetch.
 *
 * Coordinates are stored RELATIVE to `relativeTo` (the collapsed node's box at
 * collapse time). Absolute coordinates were wrong: a reflow or a user drag moves
 * the collapsed pill but not its stored subtree, so re-expanding restored the
 * children detached by the accumulated delta. `relativeTo` doubles as the format
 * marker — a payload WITHOUT it was written by the old absolute code and is
 * restored verbatim (legacy), which is no worse than what it did before.
 */
export interface RememberedSubtree {
  elements: ExcalidrawElement[];
  /** The collapsed node's origin the elements are stored relative to. */
  relativeTo?: { x: number; y: number };
}

/** Map-wide identity shared by every element: which map, source, grouping. */
export interface MindmapIdentity {
  mapId: string;
  rootPageId: string;
  source?: MindmapSource;
  groupingProperty?: string;
}

const mindmapData = (
  identity: MindmapIdentity,
  blockId: string,
  role: "node" | "connector",
  opts?: {
    stub?: boolean;
    parentBlockId?: string;
    sourcePageId?: string;
    departRatio?: number;
    provenance?: MindmapNodeProvenance;
  },
): { mindmap: MindmapCustomData } => ({
  mindmap: {
    mapId: identity.mapId,
    rootPageId: identity.rootPageId,
    blockId,
    role,
    ...(identity.source ? { source: identity.source } : {}),
    ...(identity.groupingProperty
      ? { groupingProperty: identity.groupingProperty }
      : {}),
    ...(opts?.stub ? { stub: true } : {}),
    ...(opts?.parentBlockId ? { parentBlockId: opts.parentBlockId } : {}),
    ...(opts?.sourcePageId ? { sourcePageId: opts.sourcePageId } : {}),
    ...(opts?.departRatio !== undefined
      ? { departRatio: opts.departRatio }
      : {}),
    ...(opts?.provenance ? { provenance: opts.provenance } : {}),
  },
});

/** A node box in top-left scene coordinates (matches on-canvas elements). */
export interface MindmapNodeBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Build one node element: a `mindmapNode` pill card (DOM-rendered), stamped with
 * the mindmap identity. Both block and page nodes are pills — a page pill carries
 * `pageId` so the renderer shows the Notion glyph and opens the side peek. Shared
 * by the initial emit and a refresh/expand's incremental adds so all three paths
 * produce identical-looking nodes.
 *
 * Every node whose source has children is expandable, INCLUDING the root (Fix
 * 5b): the root carries the chip so the whole map can collapse into it. A node
 * is emitted already `expanded` when its children are IN THE TREE being
 * emitted — they land on canvas with it, so its chip must read as collapse. For
 * a Notion skeleton that is the root alone (level 1 is sliced to leaves that
 * only carry `hasChildren`); for an agent tree it is every parent, since the
 * whole tree is emitted at once. A "+N more" stub is never expandable (it
 * stands in for hidden children, not a real source).
 */
export const buildMindmapNodeElement = (params: {
  identity: MindmapIdentity;
  treeNode: MindmapTree;
  box: MindmapNodeBox;
  branchColor: string;
  /** The node's parent blockId (absent on the root). */
  parentBlockId?: string;
  /** The page whose recordMap the block lives in; defaults to the map root. */
  sourcePageId?: string;
  /** Whether this node renders already expanded (default false). */
  expanded?: boolean;
}): ExcalidrawElement => {
  const { identity, treeNode, box, branchColor, parentBlockId } = params;
  const sourcePageId = params.sourcePageId ?? identity.rootPageId;

  const customData = mindmapData(identity, treeNode.blockId, "node", {
    stub: treeNode.truncated,
    parentBlockId,
    sourcePageId,
    provenance: treeNode.provenance,
  });
  if (treeNode.colorName) {
    customData.mindmap.colorName = treeNode.colorName;
  }

  return newMindmapNodeElement({
    // A native node's element id IS its blockId: the ids `mindmap_read` shows
    // are then the ids the tree ops take, with no alias table.
    ...(treeNode.kind === "text" ? { id: treeNode.blockId } : {}),
    x: box.x,
    y: box.y,
    width: box.width,
    height: box.height,
    ...INTEGRATION_STYLE,
    strokeColor: branchColor,
    label: treeNode.text,
    nodeKind: treeNode.kind,
    pageId: treeNode.pageId ?? null,
    icon: treeNode.icon ?? null,
    branchColor,
    // Chip iff the node has children — root included. Expanded iff those
    // children are emitted with it (see the header).
    expandable: treeNode.hasChildren === true,
    expanded: params.expanded ?? treeNode.children.length > 0,
    customData,
  });
};

/**
 * Build one connector arrow between a parent and child box, routed by the
 * bounded `mindmapConnectorGeometry`: it departs the parent's facing border and
 * enters the child's near border, picking a horizontal or vertical regime so an
 * extreme vertical delta can never blow up into the giant loops / full-canvas
 * lines the old half-delta elbow produced. Roundness turns the polyline into the
 * organic branch curve of a mindmap. Shared by the initial emit and a
 * refresh/expand's incremental adds so all paths route identically.
 */
/**
 * Slightly thinner than the default integration stroke (2) so map connectors
 * read as calm branch lines rather than heavy pipes, but thick enough to hold
 * the branch colour at a zoomed-out map scale (V3).
 */
const CONNECTOR_STROKE_WIDTH = 1.75;

export const buildMindmapConnectorElement = (params: {
  identity: MindmapIdentity;
  toBlockId: string;
  fromBox: MindmapNodeBox;
  toBox: MindmapNodeBox;
  strokeColor?: string;
  /** True when this connector points at a "+N more" stub, matching its node. */
  stub?: boolean;
  /**
   * Where along the parent edge to depart, as a fraction in [0,1]. The caller
   * spreads a parent's children evenly so their connectors fan out along the
   * full edge instead of piling up at the clamped center.
   */
  departRatio?: number;
}): ExcalidrawElement => {
  const {
    identity,
    toBlockId,
    fromBox,
    toBox,
    strokeColor,
    stub,
    departRatio,
  } = params;
  const { from, points } = mindmapConnectorGeometry(fromBox, toBox, {
    departRatio,
  });
  return newLinearElement({
    type: "arrow",
    x: from.x,
    y: from.y,
    points,
    // `newLinearElement` leaves width/height at 0; every other point-mutating
    // path in Excalidraw (`mutateElement`) derives them from the points, and
    // bounds-dependent code (hit-testing, binding gap math) reads them.
    ...getSizeFromPoints(points),
    ...INTEGRATION_STYLE,
    strokeWidth: CONNECTOR_STROKE_WIDTH,
    // The route is an ELBOW, not a curve: `mindmapConnectorGeometry` already
    // emits 45-degree chamfer segments where the corners round off, so the
    // point list must be drawn verbatim. `roundness` would hand it to roughjs'
    // curve generator, which smears the deliberate corners into an S again;
    // `roughness: 0` strips the hand-drawn jitter so the polyline reads clean.
    roughness: 0,
    roundness: null,
    strokeColor,
    customData: mindmapData(identity, toBlockId, "connector", {
      stub,
      departRatio,
    }),
  });
};

/**
 * The departure ratio for a child at `index` of `count` siblings: evenly spaced
 * inside the parent edge (never on the exact corners), so every child departs a
 * distinct point along the full edge. A single child departs mid-edge.
 */
export const siblingDepartRatio = (index: number, count: number): number =>
  count <= 1 ? 0.5 : (index + 1) / (count + 1);

/** Index every tree node by blockId so the emitter can look up kind/text. */
const indexTree = (tree: MindmapTree, out: Map<string, MindmapTree>): void => {
  out.set(tree.blockId, tree);
  for (const child of tree.children) {
    indexTree(child, out);
  }
};

export const emitMindmapElements = (
  layout: MindmapLayout,
  tree: MindmapTree,
  mapId: string,
  opts?: { source?: MindmapSource; groupingProperty?: string },
): ExcalidrawElement[] => {
  // The root's blockId is the Notion page/database id; every element carries it
  // so a later refresh can recover the source from any generated element.
  const identity: MindmapIdentity = {
    mapId,
    rootPageId: tree.blockId,
    source: opts?.source,
    groupingProperty: opts?.groupingProperty,
  };

  const nodesByBlock = new Map<string, MindmapTree>();
  indexTree(tree, nodesByBlock);

  // Colors begin at the first branching level (linear docs stay neutral); a
  // branch's color is inherited by its subtree. Connectors take the color of the
  // child they point to.
  const colorByBlock = assignBranchColors(tree);

  const layoutByBlock = new Map<string, MindmapLayoutNode>();
  for (const node of layout.nodes) {
    layoutByBlock.set(node.blockId, node);
  }

  // parent blockId per child, from the layout edges, so every non-root pill can
  // carry parentBlockId (a collapse reconstructs subtrees from these links).
  const parentByBlock = new Map<string, string>();
  for (const edge of layout.edges) {
    parentByBlock.set(edge.toBlockId, edge.fromBlockId);
  }

  const elements: ExcalidrawElement[] = [];

  // Nodes. Layout coordinates are box centers; elements use a top-left origin.
  for (const node of layout.nodes) {
    const treeNode = nodesByBlock.get(node.blockId);
    if (!treeNode) {
      continue;
    }
    elements.push(
      buildMindmapNodeElement({
        identity,
        treeNode,
        box: {
          x: node.x - node.width / 2,
          y: node.y - node.height / 2,
          width: node.width,
          height: node.height,
        },
        branchColor: colorByBlock.get(node.blockId) ?? MINDMAP_NEUTRAL_COLOR,
        parentBlockId: parentByBlock.get(node.blockId),
      }),
    );
  }

  // Edges. A curved arrow anchored on the parent's side edge and the child's
  // near edge. Layout coordinates are box centers; convert to top-left boxes.
  const boxOf = (node: MindmapLayoutNode): MindmapNodeBox => ({
    x: node.x - node.width / 2,
    y: node.y - node.height / 2,
    width: node.width,
    height: node.height,
  });
  // Sibling ordering per parent, so each child departs a distinct point along
  // the parent's right edge. The layout grows right-only, so a parent
  // has exactly one departure edge and the parent blockId is the whole key.
  const siblingsByParent = new Map<string, string[]>();
  for (const edge of layout.edges) {
    const list = siblingsByParent.get(edge.fromBlockId) ?? [];
    list.push(edge.toBlockId);
    siblingsByParent.set(edge.fromBlockId, list);
  }
  for (const edge of layout.edges) {
    const from = layoutByBlock.get(edge.fromBlockId);
    const to = layoutByBlock.get(edge.toBlockId);
    if (!from || !to) {
      continue;
    }
    const siblings = siblingsByParent.get(edge.fromBlockId) ?? [edge.toBlockId];
    elements.push(
      buildMindmapConnectorElement({
        identity,
        toBlockId: edge.toBlockId,
        fromBox: boxOf(from),
        toBox: boxOf(to),
        strokeColor: colorByBlock.get(edge.toBlockId),
        stub: nodesByBlock.get(edge.toBlockId)?.truncated,
        departRatio: siblingDepartRatio(
          siblings.indexOf(edge.toBlockId),
          siblings.length,
        ),
      }),
    );
  }

  // Bind each arrow to the two pills it runs between, so dragging a pill drags
  // its connectors LIVE. Runs last: binding needs the emitted element ids.
  return withMindmapBindings(elements);
};

/**
 * Store a database map's groupable properties on its root pill, returning the
 * next scene — or `null` when the root already carries exactly this list, so the
 * caller can skip an `updateScene` that would only churn autosave.
 *
 * The list is a fact about the map, so it rides the scene through save/reload
 * and the popover can open from it with no fetch at all.
 */
export const withMindmapGroupingCandidates = (
  scene: readonly ExcalidrawElement[],
  mapId: string,
  candidates: readonly Grouping[],
): ExcalidrawElement[] | null => {
  const root = findMindmapRootElement(scene, mapId);
  const mm = root ? mindmapOf(root) : undefined;
  if (!root || !mm) {
    return null;
  }
  const current = mm.groupingCandidates;
  if (
    current &&
    current.length === candidates.length &&
    current.every(
      (entry, i) =>
        entry.propertyName === candidates[i].propertyName &&
        entry.type === candidates[i].type,
    )
  ) {
    return null;
  }
  return scene.map((el) =>
    el.id === root.id
      ? newElementWith(el, {
          customData: {
            ...el.customData,
            mindmap: { ...mm, groupingCandidates: [...candidates] },
          },
        })
      : el,
  );
};

/* ------------------------------------------------------------------------- *
 * Deletion ledger
 *
 * A pill the user deletes must not come back on the next refresh. The only
 * record of that deletion used to be the element's own `isDeleted` tombstone,
 * and tombstones do not survive persistence: every save runs the scene through
 * `clearElementsForLocalStorage` → `getNonDeletedElements`, which drops them.
 * So the deletion was forgotten across a reload and refresh resurrected the pill.
 *
 * The ledger mirrors those deletions onto the map ROOT node's
 * `customData.mindmap.deletions`. The root is a LIVE element, so the memory
 * rides through save/reload with the rest of the scene. It is reconciled against
 * the live map on every refresh: an entry whose pill is live again (the user
 * undid the deletion) is dropped, because a live blockId in the ledger would
 * make the pill vanish on the following refresh.
 *
 * The ledger is ENTRY-shaped: one entry per user deletion, carrying the subtree
 * that deletion took with it. Deleting a 30-node branch is one thing the user
 * did, not thirty — a flat blockId list made the cap meaningless and would make
 * any restore pick-list list sub-children they never consciously deleted.
 * ------------------------------------------------------------------------- */

/** One user deletion: the pill removed, plus the subtree it took with it. */
export interface MindmapDeletionEntry {
  blockId: string;
  /** The pill's label at deletion time, so a restore UI can name the entry. */
  title: string;
  /** Epoch ms. Orders the ledger, so the cap evicts the oldest deletion. */
  at: number;
  /** Same-map descendants removed with it, including any it held collapsed. */
  descendants: string[];
  /** Who deleted it; absent means the user, in the editor. */
  removedBy?: "user" | "agent";
}

/** Most-recent ENTRIES kept per map; the scene is serialized on every autosave. */
export const MINDMAP_DELETION_LEDGER_CAP = 100;

/**
 * Ceiling on the descendant ids the whole ledger remembers. The entry cap alone
 * bounds nothing: each entry carries a whole subtree, so 100 branch deletions
 * could ride tens of thousands of ids through every autosave PUT and collab
 * broadcast. Over budget, the OLDEST entries lose their descendants first —
 * their own blockId stays, so the pill the user deleted is still suppressed;
 * only the sub-children of a long-forgotten deletion may reappear on a refresh.
 * The newest entry keeps its descendants even if it alone exceeds the budget:
 * clipping it would resurrect a branch the user just deleted.
 */
export const MINDMAP_DELETION_LEDGER_DESCENDANT_CAP = 5000;

/** Strip descendants from the oldest entries until the budget is met. */
export const withinDescendantBudget = (
  entries: readonly MindmapDeletionEntry[],
): MindmapDeletionEntry[] => {
  const out = [...entries];
  let budget = MINDMAP_DELETION_LEDGER_DESCENDANT_CAP;
  for (let i = out.length - 1; i >= 0; i--) {
    const count = out[i].descendants?.length ?? 0;
    if (count === 0) {
      continue;
    }
    if (count <= budget || i === out.length - 1) {
      budget -= count;
      continue;
    }
    out[i] = { ...out[i], descendants: [] };
  }
  return out;
};

const mindmapOf = (el: ExcalidrawElement): MindmapCustomData | undefined =>
  (el.customData?.mindmap as MindmapCustomData | undefined) ?? undefined;

/**
 * The live element carrying a map's ledger: the node whose blockId is the map's
 * `rootPageId`. Legacy maps generated before `rootPageId` existed fall back to
 * the only node with no parent — still the root, just identified structurally.
 */
export const findMindmapRootElement = (
  scene: readonly ExcalidrawElement[],
  mapId: string,
): ExcalidrawElement | undefined => {
  const nodes = scene.filter((el) => {
    const mm = mindmapOf(el);
    return !el.isDeleted && mm?.mapId === mapId && mm.role === "node";
  });
  const rootPageId = nodes
    .map((el) => mindmapOf(el)?.rootPageId)
    .find((id): id is string => !!id);
  return (
    (rootPageId && nodes.find((el) => mindmapOf(el)?.blockId === rootPageId)) ||
    nodes.find((el) => !mindmapOf(el)?.parentBlockId)
  );
};

/**
 * A map's ledger, migrated on read: a scene saved with the pre-entry flat array
 * yields one entry per blockId, dated 0 so those are the first the cap evicts.
 */
export const ledgerEntriesOf = (
  mm: MindmapCustomData | undefined,
): MindmapDeletionEntry[] => {
  if (mm?.deletions?.length) {
    return mm.deletions;
  }
  return (mm?.deletedBlockIds ?? []).map((blockId) => ({
    blockId,
    title: "",
    at: 0,
    descendants: [],
  }));
};

/** The deletions this map remembers the user having made, oldest first. */
export const readMindmapDeletionLedger = (
  scene: readonly ExcalidrawElement[],
  mapId: string,
): MindmapDeletionEntry[] => {
  const root = findMindmapRootElement(scene, mapId);
  return root ? ledgerEntriesOf(mindmapOf(root)) : [];
};

/** Every blockId a ledger suppresses: each entry's own block and its subtree. */
export const suppressedByMindmapLedger = (
  entries: readonly MindmapDeletionEntry[],
): Set<string> => {
  const out = new Set<string>();
  for (const entry of entries) {
    out.add(entry.blockId);
    for (const blockId of entry.descendants ?? []) {
      out.add(blockId);
    }
  }
  return out;
};

/**
 * The blockIds a map's stored ledger suppresses, minus anything live on canvas.
 *
 * The ledger is only reconciled by a refresh (`pruneMindmapDeletionLedger`), so
 * between an undo and the next refresh it still names a block whose pill is back
 * — and expand-all consults this to decide whether to descend, so the restored
 * branch's children were quietly skipped. A live pill is not a deletion,
 * whatever the ledger still says. (`tombstonedBlockIds` subtracts live ids too;
 * doing it here means every caller gets it.)
 */
export const mindmapSuppressedBlockIds = (
  scene: readonly ExcalidrawElement[],
  mapId: string,
): Set<string> => {
  const out = suppressedByMindmapLedger(
    readMindmapDeletionLedger(scene, mapId),
  );
  for (const el of scene) {
    const mm = mindmapOf(el);
    if (!el.isDeleted && mm?.mapId === mapId && mm.role === "node") {
      out.delete(mm.blockId);
    }
  }
  return out;
};

const withLedger = (
  el: ExcalidrawElement,
  deletions: MindmapDeletionEntry[],
): ExcalidrawElement => {
  const mm: MindmapCustomData = { ...mindmapOf(el)!, deletions };
  // Whatever the legacy array held is now inside `deletions`; leaving it would
  // double-count it forever, since the read prefers `deletions` but the union
  // below never sees it again.
  delete mm.deletedBlockIds;
  return newElementWith(el, {
    customData: { ...el.customData, mindmap: mm },
  });
};

/** Whether a rewritten ledger differs from what the root already carries. */
const ledgerChanged = (
  mm: MindmapCustomData | undefined,
  current: readonly MindmapDeletionEntry[],
  next: readonly MindmapDeletionEntry[],
): boolean =>
  next.length !== current.length ||
  next.some(
    (entry, i) =>
      entry.blockId !== current[i].blockId ||
      // A re-deletion keeps the same blockIds but may have trimmed descendants
      // off older entries, and that trim has to reach the scene.
      (entry.descendants?.length ?? 0) !==
        (current[i].descendants?.length ?? 0),
  ) ||
  // Same entries, but still stored in the legacy shape: migrate on this write.
  (!mm?.deletions && next.length > 0);

/**
 * Append `deletions` (mapId → entries) to each map's ledger, returning the next
 * scene or `null` when nothing changed — the caller must skip `updateScene`
 * entirely in that case, or every no-op change would churn autosave.
 */
export const recordMindmapDeletions = (
  scene: readonly ExcalidrawElement[],
  deletions: ReadonlyMap<string, readonly MindmapDeletionEntry[]>,
): ExcalidrawElement[] | null => {
  const nextByElementId = new Map<string, MindmapDeletionEntry[]>();
  for (const [mapId, entries] of deletions) {
    const root = findMindmapRootElement(scene, mapId);
    if (!root) {
      continue;
    }
    const mm = mindmapOf(root);
    const current = ledgerEntriesOf(mm);
    // Re-deleting a block replaces its entry rather than adding a second one.
    const byBlock = new Map(current.map((entry) => [entry.blockId, entry]));
    for (const entry of entries) {
      byBlock.set(entry.blockId, entry);
    }
    // Oldest deletions fall off the front: one the user has since forgotten
    // matters less than the one they just made.
    const next = withinDescendantBudget(
      [...byBlock.values()]
        .sort((a, b) => a.at - b.at)
        .slice(-MINDMAP_DELETION_LEDGER_CAP),
    );
    if (ledgerChanged(mm, current, next)) {
      nextByElementId.set(root.id, next);
    }
  }
  if (nextByElementId.size === 0) {
    return null;
  }
  return scene.map((el) => {
    const entries = nextByElementId.get(el.id);
    return entries ? withLedger(el, entries) : el;
  });
};

/**
 * Drop ledger entries whose pill is live on canvas again (an undone deletion),
 * returning the next scene or `null` when the ledger was already consistent.
 * An undo restores the whole branch in one step, so dropping the entry drops its
 * remembered descendants with it.
 */
export const pruneMindmapDeletionLedger = (
  scene: readonly ExcalidrawElement[],
  mapId: string,
): ExcalidrawElement[] | null => {
  const root = findMindmapRootElement(scene, mapId);
  const mm = root ? mindmapOf(root) : undefined;
  const current = ledgerEntriesOf(mm);
  if (!root || !current.length) {
    return null;
  }
  const live = new Set<string>();
  for (const el of scene) {
    const m = mindmapOf(el);
    if (!el.isDeleted && m?.mapId === mapId && m.role === "node") {
      live.add(m.blockId);
    }
  }
  const next = current.filter((entry) => !live.has(entry.blockId));
  return ledgerChanged(mm, current, next)
    ? scene.map((el) => (el.id === root.id ? withLedger(el, next) : el))
    : null;
};

/**
 * Forget every deletion on a map ("Restore removed nodes"), returning the next
 * scene or `null` when there was nothing to forget.
 *
 * Clearing the stored ledger is not enough on its own: `tombstonedBlockIds`
 * also unions in the map's LIVE tombstones, which are still on canvas from the
 * deletions made this session and would keep suppressing the blocks. Those are
 * restamped `removedBy: "refresh"` — precisely "may come back if Notion still
 * lists it", which is what a restore asks for. A collapse's tombstones are left
 * alone: they are a collapsed node's remembered subtree, and resurrecting them
 * would duplicate elements that node still holds.
 */
export const clearMindmapDeletionLedger = (
  scene: readonly ExcalidrawElement[],
  mapId: string,
): ExcalidrawElement[] | null => {
  const root = findMindmapRootElement(scene, mapId);
  const mm = root ? mindmapOf(root) : undefined;
  if (!root || !ledgerEntriesOf(mm).length) {
    return null;
  }
  return scene.map((el) => {
    if (el.id === root.id) {
      return withLedger(el, []);
    }
    const m = mindmapOf(el);
    if (!el.isDeleted || m?.mapId !== mapId || m.removedBy) {
      return el;
    }
    return newElementWith(el, {
      customData: {
        ...el.customData,
        mindmap: { ...m, removedBy: "refresh" },
      },
    });
  });
};

/** Recursively, the node blockIds inside a collapsed node's remembered subtree. */
const rememberedBlockIds = (el: ExcalidrawElement, out: Set<string>): void => {
  for (const child of mindmapOf(el)?.remembered?.elements ?? []) {
    const mm = mindmapOf(child);
    if (mm?.role === "node") {
      out.add(mm.blockId);
      rememberedBlockIds(child, out);
    }
  }
};

/**
 * The deletions the USER just made, as mapId → entries: elements live in `prev`
 * and tombstoned in `next`. A tombstone stamped `removedBy` came from refresh or
 * collapse, which own their own resurrection rules, so it is not a user deletion;
 * an element merely ABSENT from `next` is a scene swap (file open, reload), not
 * a deletion.
 *
 * The tombstoned set is folded into ONE entry per branch: a blockId whose parent
 * was tombstoned in the same change is a descendant of that parent's entry, not
 * a deletion of its own. A deleted node that was COLLAPSED contributes its
 * remembered subtree as descendants too — those blocks are not scene elements,
 * so nothing else would ever record them, and the node holding them is gone.
 */
export const userDeletedMindmapBlocks = (
  prev: readonly ExcalidrawElement[],
  next: readonly ExcalidrawElement[],
  now: number = Date.now(),
): Map<string, MindmapDeletionEntry[]> => {
  const out = new Map<string, MindmapDeletionEntry[]>();
  if (prev.length === 0) {
    return out;
  }
  const liveBefore = new Map<string, ExcalidrawElement>();
  for (const el of prev) {
    if (!el.isDeleted && mindmapOf(el)?.role === "node") {
      liveBefore.set(el.id, el);
    }
  }
  if (liveBefore.size === 0) {
    return out;
  }

  // Newly tombstoned nodes, keyed by blockId within their map.
  const removedByMap = new Map<string, Map<string, ExcalidrawElement>>();
  for (const el of next) {
    const mm = mindmapOf(el);
    if (!el.isDeleted || !mm || mm.removedBy || !liveBefore.has(el.id)) {
      continue;
    }
    const removed = removedByMap.get(mm.mapId) ?? new Map();
    removed.set(mm.blockId, liveBefore.get(el.id)!);
    removedByMap.set(mm.mapId, removed);
  }

  for (const [mapId, removed] of removedByMap) {
    const childrenByParent = new Map<string, string[]>();
    for (const [blockId, el] of removed) {
      const parent = mindmapOf(el)?.parentBlockId;
      if (parent && removed.has(parent)) {
        childrenByParent.set(parent, [
          ...(childrenByParent.get(parent) ?? []),
          blockId,
        ]);
      }
    }
    const entries: MindmapDeletionEntry[] = [];
    for (const [blockId, el] of removed) {
      const parent = mindmapOf(el)?.parentBlockId;
      if (parent && removed.has(parent)) {
        continue;
      }
      const descendants = new Set<string>();
      const queue = [blockId];
      while (queue.length) {
        const id = queue.shift()!;
        const node = removed.get(id);
        if (node) {
          rememberedBlockIds(node, descendants);
        }
        for (const childId of childrenByParent.get(id) ?? []) {
          if (!descendants.has(childId)) {
            descendants.add(childId);
            queue.push(childId);
          }
        }
      }
      descendants.delete(blockId);
      entries.push({
        blockId,
        title: (el as ExcalidrawMindmapNodeElement).label ?? "",
        at: now,
        descendants: [...descendants],
      });
    }
    out.set(mapId, entries);
  }
  return out;
};
